import type {
  ProbationBucket,
  ProbationCase,
  ProbationDecision,
  ProbationExtension,
  ProbationPolicy,
  ProbationPolicyInput,
  ProbationReview,
  ProbationStatus,
  ProbationSummary,
} from '@/api/services/probation.service';

export const BUCKETS: Array<{ value: ProbationBucket; label: string; hint: string }> = [
  { value: 'overdue', label: 'Overdue', hint: 'Past end date, no decision' },
  { value: 'due', label: 'Due', hint: 'Ends within 14 days' },
  { value: 'upcoming', label: 'Upcoming', hint: 'Ends in more than 14 days' },
  { value: 'extended', label: 'Extended', hint: 'Extended, still undecided' },
  { value: 'completed', label: 'Completed', hint: 'Confirmed or ended' },
];

export const PROBATION_STATUSES: readonly ProbationStatus[] = [
  'open',
  'review_submitted',
  'extended',
  'confirmed',
  'ended',
  'cancelled',
];

/** Statuses that still accept review, extension, confirmation, or ending. */
export const ACTIONABLE_STATUSES: readonly ProbationStatus[] = [
  'open',
  'review_submitted',
  'extended',
];

export const RECOMMENDATION_LABELS = {
  confirm: 'Confirm',
  extend: 'Extend',
  end: 'End probation',
} as const;

const DAY_MS = 86_400_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

/** Calendar-day difference in UTC, so an end date reads the same in every timezone. */
export function daysUntil(value: string | null | undefined, now: Date = new Date()): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const end = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((end - today) / DAY_MS);
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "in 5 days", "ends today", "3 days overdue". */
export function relativeEnd(days: number | null): string | null {
  if (days === null) return null;
  if (days === 0) return 'ends today';
  if (days > 0) return `in ${plural(days, 'day')}`;
  return `${plural(-days, 'day')} overdue`;
}

/** `YYYY-MM-DD` for date inputs, read in UTC like `formatDate`. */
export function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

/** A date input value as the UTC-midnight instant the API expects. */
export function fromDateInput(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function isActionable(status: ProbationStatus): boolean {
  return ACTIONABLE_STATUSES.includes(status);
}

function normalizeReview(value: unknown): ProbationReview | undefined {
  if (!isRecord(value)) return undefined;
  const recommendation = value.recommendation;
  return {
    reviewerUserId: str(value.reviewerUserId),
    submittedAt: str(value.submittedAt),
    rating: num(value.rating),
    strengths: str(value.strengths),
    improvements: str(value.improvements),
    recommendation:
      recommendation === 'extend' || recommendation === 'end' ? recommendation : 'confirm',
  };
}

function normalizeExtension(value: unknown): ProbationExtension | null {
  if (!isRecord(value)) return null;
  return {
    previousEndDate: str(value.previousEndDate),
    newEndDate: str(value.newEndDate),
    reason: str(value.reason),
    decidedBy: str(value.decidedBy),
    decidedAt: str(value.decidedAt),
  };
}

function normalizeDecision(value: unknown): ProbationDecision | undefined {
  if (!isRecord(value)) return undefined;
  if (value.outcome !== 'confirmed' && value.outcome !== 'ended') return undefined;
  return {
    outcome: value.outcome,
    decidedBy: str(value.decidedBy),
    decidedAt: str(value.decidedAt),
    effectiveDate: str(value.effectiveDate),
    reason: typeof value.reason === 'string' ? value.reason : undefined,
  };
}

/**
 * Coerces one case from the API into a renderable shape, or `null` when it has
 * no id. Missing arrays and objects become empty values so a partial payload
 * degrades to dashes rather than crashing the tab.
 */
export function normalizeCase(value: unknown): ProbationCase | null {
  if (!isRecord(value) || typeof value._id !== 'string' || !value._id) return null;
  const employee = isRecord(value.employee) ? value.employee : {};
  const status = PROBATION_STATUSES.includes(value.status as ProbationStatus)
    ? (value.status as ProbationStatus)
    : 'open';
  const bucket = BUCKETS.some((item) => item.value === value.bucket)
    ? (value.bucket as ProbationBucket)
    : 'upcoming';
  return {
    _id: value._id,
    companyId: str(value.companyId),
    companyName: str(value.companyName),
    userId: str(value.userId),
    employmentId: str(value.employmentId),
    status,
    startDate: str(value.startDate),
    originalEndDate: str(value.originalEndDate),
    currentEndDate: str(value.currentEndDate),
    review: normalizeReview(value.review),
    extensions: (Array.isArray(value.extensions) ? value.extensions : [])
      .map(normalizeExtension)
      .filter((item): item is ProbationExtension => item !== null),
    decision: normalizeDecision(value.decision),
    policySnapshot: isRecord(value.policySnapshot)
      ? (value.policySnapshot as ProbationCase['policySnapshot'])
      : undefined,
    bucket,
    employee: {
      userId: str(employee.userId) || str(value.userId),
      name: str(employee.name) || str(employee.email) || 'Unknown employee',
      email: str(employee.email),
      avatarUrl: typeof employee.avatarUrl === 'string' ? employee.avatarUrl : undefined,
    },
  };
}

export function normalizeSummary(value: unknown): ProbationSummary {
  const source = isRecord(value) ? value : {};
  return Object.fromEntries(
    BUCKETS.map(({ value: bucket }) => [bucket, Math.max(0, num(source[bucket]))])
  ) as ProbationSummary;
}

/** Server defaults (phase 4 spec), used only to fill gaps in a partial payload. */
export const DEFAULT_POLICY: ProbationPolicyInput = {
  durationDays: 90,
  reminderDaysBefore: [14, 7, 1],
  escalateAfterDaysOverdue: 3,
  maxExtensions: 2,
  maxExtensionDays: 90,
  reviewRequired: true,
};

export function normalizePolicy(value: unknown, companyId: string): ProbationPolicy {
  const source = isRecord(value) ? value : {};
  const reminders = Array.isArray(source.reminderDaysBefore)
    ? source.reminderDaysBefore.filter(
        (day): day is number => typeof day === 'number' && Number.isFinite(day)
      )
    : DEFAULT_POLICY.reminderDaysBefore;
  return {
    companyId: str(source.companyId) || companyId,
    durationDays: num(source.durationDays, DEFAULT_POLICY.durationDays),
    reminderDaysBefore: reminders,
    escalateAfterDaysOverdue: num(
      source.escalateAfterDaysOverdue,
      DEFAULT_POLICY.escalateAfterDaysOverdue
    ),
    maxExtensions: num(source.maxExtensions, DEFAULT_POLICY.maxExtensions),
    maxExtensionDays: num(source.maxExtensionDays, DEFAULT_POLICY.maxExtensionDays),
    reviewRequired:
      typeof source.reviewRequired === 'boolean'
        ? source.reviewRequired
        : DEFAULT_POLICY.reviewRequired,
    // Without a saved record the server is applying defaults, so say so.
    isDefault: typeof source.isDefault === 'boolean' ? source.isDefault : true,
    updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : undefined,
  };
}

export interface CaseLimits {
  /** `null` when neither the snapshot nor the policy is known; the server still enforces it. */
  maxExtensions: number | null;
  maxExtensionDays: number | null;
  reviewRequired: boolean;
}

/**
 * The limits that govern one case: its snapshot where the server recorded one,
 * the company policy otherwise. Unknown limits are left to the server rather
 * than guessed, so a missing policy never blocks a legitimate action.
 */
export function effectiveLimits(
  probationCase: ProbationCase,
  policy: ProbationPolicy | undefined
): CaseLimits {
  const snapshot = probationCase.policySnapshot ?? {};
  const pick = (key: 'maxExtensions' | 'maxExtensionDays'): number | null => {
    const fromSnapshot = snapshot[key];
    if (typeof fromSnapshot === 'number' && Number.isFinite(fromSnapshot)) return fromSnapshot;
    return policy ? policy[key] : null;
  };
  return {
    maxExtensions: pick('maxExtensions'),
    maxExtensionDays: pick('maxExtensionDays'),
    reviewRequired:
      typeof snapshot.reviewRequired === 'boolean'
        ? snapshot.reviewRequired
        : (policy?.reviewRequired ?? false),
  };
}

/**
 * "14, 7, 1" → [14, 7, 1]: whole positive days, de-duplicated, latest first.
 * Returns `null` when any entry is not a whole positive number.
 */
export function parseReminderDays(text: string): number[] | null {
  const parts = text
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const days: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return null;
    const value = Number(part);
    if (value < 1) return null;
    if (!days.includes(value)) days.push(value);
  }
  return days.sort((a, b) => b - a);
}

/** "14, 7 and 1 days before it ends", or "No reminders" for an empty list. */
export function describeReminderDays(days: number[]): string {
  if (days.length === 0) return 'No reminders';
  const list = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(
    days.map(String)
  );
  return `${list} ${days.length === 1 && days[0] === 1 ? 'day' : 'days'} before it ends`;
}
